import { FiGithub, FiTerminal, FiCode, FiArrowLeft } from 'react-icons/fi';
import NeonButton from '../UI/NeonButton';
import Panel from '../UI/Panel';
import Tag from '../UI/Tag';
import Window from '../Window/Window';

export default function Styleguide() {
  return (
    <div className="min-h-screen bg-[var(--bg-deep)] text-[var(--text-primary)] pb-24">
      {/* HEADER */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-[var(--bg-glass-strong)] border-b border-[var(--neon-cyan)]/20 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="font-[Orbitron] text-sm tracking-[0.3em] text-[var(--neon-cyan)] uppercase">
            Blade&nbsp;OS
          </span>
          <span className="w-px h-5 bg-[var(--neon-cyan)]/20" />
          <span className="text-xs font-mono text-[var(--text-muted)] uppercase tracking-wider">
            /styleguide
          </span>
        </div>
        <a
          href="#/"
          className="inline-flex items-center gap-2 text-xs font-mono text-[var(--neon-cyan)] hover:text-[var(--text-primary)] transition-colors"
        >
          <FiArrowLeft size={14} /> back to desktop
        </a>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-16">
        {/* INTRO */}
        <section>
          <h1 className="font-[Orbitron] text-3xl sm:text-4xl uppercase tracking-wider text-[var(--neon-cyan)] text-glow-cyan mb-3">
            Design System
          </h1>
          <p className="text-[var(--text-secondary)] max-w-2xl">
            Cyberpunk OS v2.0 — tokens, components, primitives.
            All assets live in <code className="text-[var(--neon-violet)]">src/components/UI</code> + <code className="text-[var(--neon-violet)]">src/components/Window</code>.
          </p>
        </section>

        {/* BUTTONS */}
        <Section title="Buttons" subtitle="NeonButton — 5 variants × 4 sizes">
          <Panel tone="cyan" padding="lg">
            <SubLabel>Variants</SubLabel>
            <div className="flex flex-wrap gap-3 mb-6">
              <NeonButton variant="primary">Primary</NeonButton>
              <NeonButton variant="secondary">Secondary</NeonButton>
              <NeonButton variant="ghost">Ghost</NeonButton>
              <NeonButton variant="danger">Danger</NeonButton>
              <NeonButton variant="violet">Violet</NeonButton>
            </div>
            <SubLabel>Sizes</SubLabel>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <NeonButton size="sm">Small</NeonButton>
              <NeonButton size="md">Medium</NeonButton>
              <NeonButton size="lg">Large</NeonButton>
              <NeonButton size="xl">X-Large</NeonButton>
            </div>
            <SubLabel>With icons</SubLabel>
            <div className="flex flex-wrap gap-3">
              <NeonButton icon={<FiGithub />}>GitHub</NeonButton>
              <NeonButton variant="violet" icon={<FiTerminal />}>Open Terminal</NeonButton>
              <NeonButton variant="secondary" iconRight={<FiCode />}>Source</NeonButton>
            </div>
          </Panel>
        </Section>

        {/* PANELS */}
        <Section title="Panels" subtitle="Glassmorphism containers with neon border + glow">
          <div className="grid sm:grid-cols-3 gap-4">
            <Panel tone="cyan" padding="lg">
              <h3 className="font-[Orbitron] text-sm uppercase tracking-wider text-[var(--neon-cyan)] mb-2">Cyan Tone</h3>
              <p className="text-xs text-[var(--text-secondary)]">Default. Cyan border + cyan→violet top glow on hover.</p>
            </Panel>
            <Panel tone="pink" padding="lg">
              <h3 className="font-[Orbitron] text-sm uppercase tracking-wider text-[var(--neon-pink)] mb-2">Pink Tone</h3>
              <p className="text-xs text-[var(--text-secondary)]">Pink border + pink→violet top glow. Use for danger/contact.</p>
            </Panel>
            <Panel tone="violet" padding="lg">
              <h3 className="font-[Orbitron] text-sm uppercase tracking-wider text-[var(--neon-violet)] mb-2">Violet Tone</h3>
              <p className="text-xs text-[var(--text-secondary)]">Violet border + violet→cyan top glow. Use for projects/skills.</p>
            </Panel>
          </div>
        </Section>

        {/* TAGS */}
        <Section title="Tags" subtitle="Skill badges, status pills, inline labels">
          <Panel tone="violet" padding="lg">
            <SubLabel>Tech</SubLabel>
            <div className="flex flex-wrap gap-2 mb-4">
              <Tag tone="cyan">React</Tag>
              <Tag tone="cyan">TypeScript</Tag>
              <Tag tone="violet">FastAPI</Tag>
              <Tag tone="violet">PostgreSQL</Tag>
              <Tag tone="pink">Cybersecurity</Tag>
              <Tag tone="green">Docker</Tag>
              <Tag tone="amber">Java</Tag>
              <Tag tone="muted">archived</Tag>
            </div>
            <SubLabel>Status</SubLabel>
            <div className="flex flex-wrap gap-2">
              <Tag tone="green">● online</Tag>
              <Tag tone="amber">● in progress</Tag>
              <Tag tone="muted">○ deprecated</Tag>
              <Tag tone="pink">● critical</Tag>
            </div>
          </Panel>
        </Section>

        {/* WINDOW */}
        <Section title="Window" subtitle="react-rnd wrapper — draggable + resizable, macOS traffic lights">
          <Panel tone="cyan" padding="lg">
            <div className="relative h-[460px] overflow-hidden rounded-xl border border-[var(--neon-cyan)]/10">
              <Window
                id="styleguide-demo"
                title="about.exe"
                icon={<FiCode />}
                position={{ x: 40, y: 30 }}
                size={{ width: 460, height: 320 }}
                tone="cyan"
              >
                <div className="p-6 font-mono text-xs text-[var(--text-secondary)] space-y-2">
                  <p className="text-[var(--neon-cyan)]">// Cyberpunk Window component</p>
                  <p>Draggable header · macOS-style traffic lights · resize handles</p>
                  <p>Tone variants: <span className="text-[var(--neon-cyan)]">cyan</span> · <span className="text-[var(--neon-pink)]">pink</span> · <span className="text-[var(--neon-violet)]">violet</span> · <span className="text-[var(--neon-green)]">green</span></p>
                  <p className="pt-2 text-[var(--text-muted)]">try dragging the title bar ↗</p>
                </div>
              </Window>
              <Window
                id="styleguide-demo-2"
                title="terminal.tty"
                icon={<FiTerminal />}
                position={{ x: 240, y: 160 }}
                size={{ width: 380, height: 240 }}
                tone="violet"
              >
                <div className="p-4 font-mono text-xs">
                  <p className="text-[var(--neon-green)]">$ ./start --desktop</p>
                  <p className="text-[var(--text-secondary)]">Initializing neon palette...</p>
                  <p className="text-[var(--text-secondary)]">Mounting matrix wallpaper...</p>
                  <p className="text-[var(--neon-cyan)]">Welcome back, Blade.</p>
                  <p className="text-[var(--neon-pink)]">▌</p>
                </div>
              </Window>
            </div>
          </Panel>
        </Section>

        {/* TOKENS */}
        <Section title="Tokens" subtitle="Design tokens live in src/styles/index.css (@theme block)">
          <Panel tone="cyan" padding="lg">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { name: 'neon-cyan', value: '#00f0ff' },
                { name: 'neon-pink', value: '#ff2a6d' },
                { name: 'neon-violet', value: '#a855f7' },
                { name: 'neon-green', value: '#39ff14' },
                { name: 'neon-amber', value: '#f59e0b' },
                { name: 'bg-deep', value: '#05010a' },
              ].map((t) => (
                <div key={t.name} className="flex items-center gap-3 p-2 rounded bg-black/30 border border-white/5">
                  <span
                    className="w-8 h-8 rounded shrink-0 border border-white/10"
                    style={{ background: t.value, boxShadow: `0 0 12px ${t.value}40` }}
                  />
                  <div className="min-w-0">
                    <p className="font-mono text-[0.7rem] text-[var(--text-primary)] truncate">{t.name}</p>
                    <p className="font-mono text-[0.65rem] text-[var(--text-muted)]">{t.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </Section>
      </main>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section>
      <div className="mb-4">
        <h2 className="font-[Orbitron] text-xl uppercase tracking-wider text-[var(--text-primary)] mb-1">
          {title}
        </h2>
        <p className="text-xs font-mono text-[var(--text-muted)] uppercase tracking-wider">
          {subtitle}
        </p>
      </div>
      {children}
    </section>
  );
}

function SubLabel({ children }) {
  return (
    <p className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2 mt-3 first:mt-0">
      {children}
    </p>
  );
}
